import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, Button, TextField, Typography, useTheme } from '@mui/material';
import useSirenSearch, { CompanyType } from '../../../hooks/useSirenSearch';

export type SirenAutocompleteProps = {
    placeholder: string;
    onChange?: (siren: string) => void;
    onSelectItem: (company: CompanyType) => void;
    hasError?: boolean;
    onError?: (message: string) => void;
    defaultValue?: string;
};

const SirenAutocomplete: React.FunctionComponent<SirenAutocompleteProps> = function (props) {
    const {
        defaultValue,
        placeholder,
        onChange,
        onSelectItem,
        hasError = false,
        onError = () => {},
    } = props;
    const [value, setValue] = useState<CompanyType | Partial<CompanyType> | null>({
        siren: defaultValue ?? '',
    });
    const [inputValue, setInputValue] = useState(defaultValue ?? '');
    const [options, setOptions] = useState<Array<CompanyType>>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const { fetchSuggestions } = useSirenSearch();
    const theme = useTheme();
    const { palette } = theme;

    useEffect(() => {
        const refreshSuggestions = async () => {
            fetchSuggestions(inputValue, (suggestions: Array<CompanyType>, err: string) => {
                setOptions(suggestions || []);
                setLoading(false);

                if (err) {
                    onError(err);
                }
            });
        };

        if (!/^\d{9}(\d{5})?$/.test(inputValue)) {
            setOptions([]);
            return;
        }

        setLoading(true);
        refreshSuggestions();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, inputValue, fetchSuggestions]);

    return (
        <Autocomplete
            freeSolo
            getOptionLabel={(option: CompanyType) => option?.siret || option?.siren || ''}
            filterOptions={(x) => x}
            options={options}
            autoComplete
            includeInputInList
            filterSelectedOptions
            value={value}
            loading={loading}
            clearIcon={null}
            loadingText="Chargement..."
            noOptionsText="Aucun résultat pour votre recherche"
            autoHighlight
            onChange={(event: React.FormEvent<HTMLInputElement>, newValue: CompanyType | null) => {
                if (newValue) {
                    setOptions(newValue ? [newValue, ...options] : options);
                    setValue(newValue);
                } else {
                    setOptions([]);
                    setValue(null);
                }
            }}
            onInputChange={(event, newInputValue) => {
                const valueInput = newInputValue.replace(/\s/g, '');
                setInputValue(valueInput);
                if (onChange) {
                    onChange(valueInput);
                }
            }}
            renderInput={(params) => (
                <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: '0.5em' }}>
                    <TextField
                        error={hasError}
                        {...params}
                        placeholder={placeholder}
                    />
                    <Typography variant="l2dbodyxs">
                        Astuce: saisissez un SIRET pour cibler un établissement précis.
                    </Typography>
                </Box>
            )}
            renderOption={(childProps, option) => {
                const { key, ...optionProps } = childProps;
                const label = option.description;

                return (
                    <li
                        key={key}
                        {...optionProps}
                    >
                        <Button
                            sx={{ textAlign: 'left', color: palette.text.primary }}
                            onClick={() => onSelectItem(option)}
                        >
                            {label}
                        </Button>
                    </li>
                );
            }}
        />
    );
};

export default SirenAutocomplete;
